import { useMemo, useState } from 'react';
import { compactJson, formatCost, formatDuration, formatTokens, usageTotal } from '../lib/format';
import { sumStepUsage } from '../lib/run-state';
import type { RawEventRecord, RunState, UsageMetrics } from '../lib/types';
import { ChevronIcon, CloseIcon, CopyIcon } from './Icons';

type Tab = 'process' | 'reasoning' | 'tokens' | 'events';

interface RunInspectorProps {
  run?: RunState;
  mobileOpen: boolean;
  onClose: () => void;
}

function statusLabel(run: RunState): string {
  if (run.status === 'streaming') return 'Live';
  if (run.status === 'complete') return 'Complete';
  if (run.status === 'restored') return 'Restored';
  if (run.status === 'aborted') return 'Stopped';
  return 'Failed';
}

function ExecutionRail({ run }: { run: RunState }) {
  if (run.trace.length === 0) {
    return <div className="inspector-empty compact"><p>Execution telemetry was not stored with this historical message.</p></div>;
  }
  return (
    <ol className="execution-rail">
      {run.trace.map((entry, index) => (
        <li key={entry.id} className={entry.status}>
          <div className="rail-axis">
            <span className="rail-index">{String(index + 1).padStart(2, '0')}</span>
            <i />
          </div>
          <div className="rail-event">
            <div>
              <strong>{entry.label}</strong>
              <time>+{formatDuration(entry.atMs)}</time>
            </div>
            {entry.detail && <p>{entry.detail}</p>}
            {entry.durationMs !== undefined && <small>{formatDuration(entry.durationMs)}</small>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function JsonValue({ value }: { value: unknown }) {
  if (value === undefined) return <span className="muted">No value returned</span>;
  return <pre className="json-value">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>;
}

function ProcessPanel({ run }: { run: RunState }) {
  return (
    <div className="inspector-panel process-panel">
      <section className="panel-section">
        <div className="section-heading"><span>Execution rail</span><b>{run.trace.length}</b></div>
        <ExecutionRail run={run} />
      </section>

      {run.route && (
        <section className="panel-section detail-block">
          <div className="section-heading"><span>Routing decision</span><b>{run.route.primitiveId ?? 'direct'}</b></div>
          <dl className="key-value-list">
            <div><dt>Primitive</dt><dd>{run.route.primitiveType ?? 'none'} / {run.route.primitiveId ?? 'lead'}</dd></div>
            {run.route.prompt && <div><dt>Delegated task</dt><dd>{run.route.prompt}</dd></div>}
            {run.route.rationale && <div><dt>Selection rationale</dt><dd>{run.route.rationale}</dd></div>}
          </dl>
        </section>
      )}

      {run.tools.length > 0 && (
        <section className="panel-section">
          <div className="section-heading"><span>Tool activity</span><b>{run.tools.length}</b></div>
          <div className="tool-stack">
            {run.tools.map((tool) => (
              <details className="tool-card" key={tool.id}>
                <summary>
                  <span className={`tool-state ${tool.status}`} />
                  <span><strong>{tool.name}</strong><small>{compactJson(tool.args ?? tool.argsText, 72) || 'No arguments'}</small></span>
                  <span>{tool.endedAtMs !== undefined ? formatDuration(tool.endedAtMs - tool.startedAtMs) : 'running'}</span>
                  <ChevronIcon />
                </summary>
                <div className="tool-detail">
                  <label>Arguments</label>
                  <JsonValue value={tool.args ?? tool.argsText} />
                  <label>Result</label>
                  <JsonValue value={tool.result} />
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {run.validation && (
        <section className={`panel-section validation-block ${run.validation.passed === false ? 'failed' : 'passed'}`}>
          <div className="section-heading"><span>Network validation</span><b>{run.validation.passed === false ? 'Failed' : 'Passed'}</b></div>
          {run.validation.reason && <p>{run.validation.reason}</p>}
          <div className="validation-meta">
            {run.validation.score !== undefined && <span>score {run.validation.score}</span>}
            {run.validation.durationMs !== undefined && <span>{formatDuration(run.validation.durationMs)}</span>}
            {run.validation.timedOut && <span>timed out</span>}
          </div>
        </section>
      )}
    </div>
  );
}

function ReasoningPanel({ run }: { run: RunState }) {
  const [copied, setCopied] = useState(false);
  const words = run.reasoning.trim() ? run.reasoning.trim().split(/\s+/).length : 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(run.reasoning);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="inspector-panel reasoning-panel">
      <div className="reasoning-toolbar">
        <span>{formatTokens(words)} words · {formatTokens(run.reasoning.length)} characters</span>
        <button type="button" onClick={copy} disabled={!run.reasoning}><CopyIcon />{copied ? 'Copied' : 'Copy'}</button>
      </div>
      {run.reasoning ? (
        <pre className={`reasoning-stream${run.status === 'streaming' ? ' live' : ''}`}>{run.reasoning}</pre>
      ) : (
        <div className="inspector-empty">
          <span className="empty-glyph">R/0</span>
          <h3>No reasoning text received</h3>
          <p>The run may not expose reasoning, or this message was restored without its original event stream.</p>
        </div>
      )}
    </div>
  );
}

function UsageCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return <div className={`usage-card${accent ? ' accent' : ''}`}><span>{label}</span><strong>{value}</strong></div>;
}

function estimatedCost(run: RunState): number | undefined {
  if (run.totalUsage?.cost !== undefined) return run.totalUsage.cost;
  const costs = [run.route?.usage?.cost, ...run.steps.map((step) => step.usage?.cost)]
    .filter((value): value is number => typeof value === 'number');
  return costs.length ? costs.reduce((total, value) => total + value, 0) : undefined;
}

function UsageRow({ label, usage, provider, model }: { label: string; usage?: UsageMetrics; provider?: string; model?: string }) {
  return (
    <tr>
      <td><strong>{label}</strong>{(provider || model) && <small>{[provider, model].filter(Boolean).join(' · ')}</small>}</td>
      <td>{formatTokens(usage?.inputTokens)}</td>
      <td>{formatTokens(usage?.outputTokens)}</td>
      <td>{formatTokens(usage?.reasoningTokens)}</td>
      <td>{formatTokens(usage?.cachedInputTokens)}</td>
      <td>{formatCost(usage?.cost)}</td>
    </tr>
  );
}

function TokensPanel({ run }: { run: RunState }) {
  const summed = sumStepUsage(run.steps);
  const total = run.totalUsage ?? summed;
  const cacheRate = total?.inputTokens && total.cachedInputTokens !== undefined
    ? Math.round((total.cachedInputTokens / total.inputTokens) * 100)
    : undefined;
  return (
    <div className="inspector-panel tokens-panel">
      <div className="usage-grid">
        <UsageCard label="Total tokens" value={formatTokens(usageTotal(total))} accent />
        <UsageCard label="Input" value={formatTokens(total?.inputTokens)} />
        <UsageCard label="Output" value={formatTokens(total?.outputTokens)} />
        <UsageCard label="Reasoning" value={formatTokens(total?.reasoningTokens)} />
        <UsageCard label="Cached input" value={formatTokens(total?.cachedInputTokens)} />
        <UsageCard label="Reported cost" value={formatCost(estimatedCost(run))} />
      </div>
      {cacheRate !== undefined && (
        <section className="cache-meter">
          <div><span>Prompt cache hit rate</span><strong>{cacheRate}%</strong></div>
          <i><span style={{ width: `${Math.min(100, cacheRate)}%` }} /></i>
        </section>
      )}
      <section className="panel-section usage-breakdown">
        <div className="section-heading"><span>Usage by stage</span><b>{run.steps.length + (run.route?.usage ? 1 : 0)}</b></div>
        {(run.route?.usage || run.steps.some((step) => step.usage)) ? (
          <div className="table-scroll">
            <table>
              <thead><tr><th>Stage</th><th>Input</th><th>Output</th><th>Reason</th><th>Cached</th><th>Cost</th></tr></thead>
              <tbody>
                {run.route?.usage && <UsageRow label="Lead routing" usage={run.route.usage} />}
                {run.steps.map((step) => (
                  <UsageRow key={step.id} label={`Model step ${step.index}`} usage={step.usage} provider={step.provider} model={step.model} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="inspector-empty compact"><p>No token metadata was retained for this run.</p></div>
        )}
      </section>
      <p className="usage-note">
        Network totals are the authoritative aggregate reported by Mastra. Per-stage cost appears only when the model provider includes it in step metadata.
      </p>
    </div>
  );
}

function RawEventRow({ event }: { event: RawEventRecord }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="event-row" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        <span>{String(event.sequence).padStart(3, '0')}</span>
        <strong>{event.type}</strong>
        <time>+{formatDuration(event.atMs)}</time>
        {event.from && <small>{event.from}</small>}
        <ChevronIcon />
      </summary>
      {open && <pre>{JSON.stringify(event.raw, null, 2)}</pre>}
    </details>
  );
}

function EventsPanel({ run }: { run: RunState }) {
  const [query, setQuery] = useState('');
  const [includeDeltas, setIncludeDeltas] = useState(false);
  const events = useMemo(() => run.eventLog.filter((event) => {
    if (!includeDeltas && event.type.includes('delta')) return false;
    return !query || event.type.toLowerCase().includes(query.toLowerCase());
  }), [includeDeltas, query, run.eventLog]);
  const deltaCount = run.eventLog.filter((event) => event.type.includes('delta')).length;

  return (
    <div className="inspector-panel events-panel">
      <div className="event-controls">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter event types…" aria-label="Filter events" />
        <label><input type="checkbox" checked={includeDeltas} onChange={(event) => setIncludeDeltas(event.target.checked)} />Include {deltaCount} deltas</label>
      </div>
      <div className="event-counts" aria-label="Event type counts">
        {Object.entries(run.eventCounts).map(([type, count]) => <button type="button" key={type} onClick={() => setQuery(type)}><span>{type}</span><b>{count}</b></button>)}
      </div>
      <p className="redaction-note">Sensitive header and credential fields are redacted before display.</p>
      {run.eventLog.length === 0 ? (
        <div className="inspector-empty">
          <span className="empty-glyph">E/0</span>
          <h3>Raw events unavailable</h3>
          <p>Event payloads stay in memory for the current page session and are intentionally omitted from local storage.</p>
        </div>
      ) : events.length === 0 ? (
        <div className="inspector-empty compact"><p>No events match this filter.</p></div>
      ) : (
        <div className="event-list">{events.map((event) => <RawEventRow key={event.sequence} event={event} />)}</div>
      )}
    </div>
  );
}

export function RunInspector({ run, mobileOpen, onClose }: RunInspectorProps) {
  const [tab, setTab] = useState<Tab>('process');
  const duration = run ? (run.endedAt ?? Date.now()) - run.startedAt : undefined;
  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'process', label: 'Process', count: run?.trace.length },
    { id: 'reasoning', label: 'Reasoning' },
    { id: 'tokens', label: 'Tokens' },
    { id: 'events', label: 'Events', count: run?.totalEvents },
  ];

  return (
    <aside className={`run-inspector${mobileOpen ? ' mobile-open' : ''}`} aria-label="Run inspector">
      <div className="inspector-header">
        <div>
          <span className="inspector-eyebrow">Selected execution</span>
          <strong>{run ? `run/${run.id.slice(0, 8)}` : 'No run selected'}</strong>
        </div>
        <button className="icon-button inspector-close" type="button" onClick={onClose} aria-label="Close inspector"><CloseIcon /></button>
      </div>
      {run ? (
        <>
          <div className="run-ledger">
            <span className={`run-status ${run.status}`}>{statusLabel(run)}</span>
            <span><small>Agent</small><strong>{run.agentId}</strong></span>
            <span><small>Elapsed</small><strong>{formatDuration(duration)}</strong></span>
            <span><small>Events</small><strong>{run.totalEvents}</strong></span>
          </div>
          <div className="inspector-tabs" role="tablist" aria-label="Run details">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                onClick={() => setTab(item.id)}
              >
                {item.label}{item.count !== undefined && <b>{item.count}</b>}
              </button>
            ))}
          </div>
          <div className="inspector-scroll">
            {tab === 'process' && <ProcessPanel run={run} />}
            {tab === 'reasoning' && <ReasoningPanel run={run} />}
            {tab === 'tokens' && <TokensPanel run={run} />}
            {tab === 'events' && <EventsPanel run={run} />}
          </div>
        </>
      ) : (
        <div className="inspector-empty inspector-welcome">
          <span className="empty-glyph">RUN/—</span>
          <h3>The execution will land here</h3>
          <p>Send a message, then inspect its live process, reasoning, token usage, tool calls, validation, and raw Mastra events.</p>
        </div>
      )}
    </aside>
  );
}
